import { faTimesCircle } from '@fortawesome/pro-thin-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Chip, FormControl, MenuItem, Select, Stack, styled } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import type { ProductListAPIResponse } from '@partful/shared-types';
import { ComponentProps } from 'react';
import { StyledSelectField } from 'src/components/Select';
import { ProductFile } from '../Uploader';

type Props = Readonly<
  ComponentProps<typeof Select> & {
    products: ProductListAPIResponse;
    file: ProductFile;
    editFileDetails: (newDetails: Partial<ProductFile>) => void;
  }
>;

export function FileDetailsProductDropdown({ products, file, editFileDetails, ...props }: Props) {
  const currentProducts = file.products ?? [];
  const selected = currentProducts.map(/* istanbul ignore next */ (product) => product.productId);

  /* istanbul ignore next */
  const handleProductChange = (event: SelectChangeEvent<unknown>) => {
    const newProductIds = event.target.value as string[];
    const newProductId = newProductIds[newProductIds.length - 1];
    if (selected.includes(newProductId)) return;

    const newProduct = products.find((product) => product.productId === newProductId);
    if (!newProduct) return;

    const newProducts = [...currentProducts, newProduct];
    editFileDetails({ products: newProducts });
  };

  /* istanbul ignore next */
  const handleDeleteFactory = (productId: string) => () => {
    const newProducts = currentProducts.filter((product) => product.productId !== productId);

    editFileDetails({ products: newProducts });
  };

  const shouldDisable = currentProducts.length > 0;

  return (
    <FormControl variant="filled">
      <RowSelectField
        multiple
        value={selected}
        onChange={handleProductChange}
        renderValue={
          /* istanbul ignore next */ (selected) => (
            <Stack gap={1} direction="row" flexWrap="wrap">
              {(selected as string[]).map((value) => (
                <Chip
                  key={value}
                  label={value}
                  onDelete={handleDeleteFactory(value)}
                  deleteIcon={<FontAwesomeIcon icon={faTimesCircle} onMouseDown={(event) => event.stopPropagation()} />}
                />
              ))}
            </Stack>
          )
        }
        name="productId"
        data-testid="product-select"
        {...props}
      >
        {products.map(
          /* istanbul ignore next */ ({ productId, name }) => (
            <MenuItem value={productId || ''} key={productId} disabled={shouldDisable}>
              {name}
            </MenuItem>
          )
        )}
      </RowSelectField>
    </FormControl>
  );
}

export const RowSelectField = styled(StyledSelectField)`
  width: 100%;
  min-height: 3.3rem;
  .MuiFilledInput-input {
    padding: 0.6rem;
  }
`;
